"use client";

import React from "react";
import { Mail, MapPin, Phone, Send, MessageSquare, Building2, User, Smartphone } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="6817" className="flex flex-col items-center justify-center h-full min-h-[400px] text-center p-8 space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-full bg-secondary/20 flex items-center justify-center text-secondary mb-4">
          <Send className="size-8" />
        </div>
        <h3 className="text-2xl font-bold text-foreground">Inquiry Received!</h3>
        <p className="text-muted-foreground max-w-xs mx-auto">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-primary text-primary hover:bg-primary hover:text-primary-foreground">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {}
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Your Name</FormLabel>
              <FormControl>
                <div className="relative">
                  <User className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                  <Input placeholder="Jane Doe" className="pl-9 border-muted bg-background focus-visible:ring-primary" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Email Address</FormLabel>
              <FormControl>
                <div className="relative">
                  <Mail className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                  <Input type="email" placeholder="jane@investkc.com" className="pl-9 border-muted bg-background focus-visible:ring-primary" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {}
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Phone Number</FormLabel>
              <FormControl>
                <div className="relative">
                  <Smartphone className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                  <Input type="tel" placeholder="555-123-4567" className="pl-9 border-muted bg-background focus-visible:ring-primary" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="organization" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Property Address</FormLabel>
              <FormControl>
                <div className="relative">
                  <MapPin className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                  <Input placeholder="123 Main St, Kansas City, MO" className="pl-9 border-muted bg-background focus-visible:ring-primary" {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="subject" rules={{
      required: "Subject is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Number of Units</FormLabel>
            <FormControl>
              <div className="relative">
                <Building2 className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                <Input placeholder="e.g. Single Family, 4-plex, or 12 units" className="pl-9 border-muted bg-background focus-visible:ring-primary" {...field} />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Message</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your management needs or investment goals..." className="min-h-[120px] resize-y border-muted bg-background focus-visible:ring-primary" {...field} />
            </FormControl>
            <FormDescription className="text-xs">Include any specific services you're interested in.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto bg-primary text-primary-foreground hover:bg-secondary hover:text-secondary-foreground transition-all duration-300 rounded" size="lg">
        {isSubmitting ? <>
            <span className="animate-spin mr-2"></span> Sending...
          </> : <>
            Request Consultation <Send className="ml-2 size-4" />
          </>}
      </Button>
    </Form>;
}
export default function LetsConnect() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const contactInfo = [{
    icon: Mail,
    label: "Email Us",
    value: "info@investkcpm.com",
    href: "mailto:info@investkcpm.com",
    description: "Direct line to our management team."
  }, {
    icon: Phone,
    label: "Call Us",
    value: "(816) 600-7900",
    href: "tel:+18166007900",
    description: "Mon-Fri from 8am to 5pm CST."
  }, {
    icon: MapPin,
    label: "Visit HQ",
    value: "5545 N Oak Trfy, Suite 12C,\xA0 \xA0 \xA0 Kansas City, MO 64118",
    href: "#",
    description: "Located in the heart of the KC Metro."
  }];
  return <section id="lets-talk-property" className="bg-background text-foreground py-20 px-4 md:px-8 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-secondary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        <div ref={ref} className={`transition-all duration-1000 ease-out transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          {}
          <div className="bg-card backdrop-blur-md border border-border rounded shadow-xl overflow-hidden">
            <div className="grid lg:grid-cols-5 min-h-[600px]">
              
              {}
              <div className="lg:col-span-2 bg-primary p-8 md:p-12 flex flex-col justify-between border-b lg:border-b-0 lg:border-r border-white/10">
                <div>
                  <div className="inline-flex items-center justify-center p-3 bg-secondary rounded mb-6 text-secondary-foreground">
                    <MessageSquare className="size-6" />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold tracking-tighter mb-4 text-primary-foreground">
                    Let's Talk Property
                  </h2>
                  <p className="text-primary-foreground/80 mb-10 leading-relaxed font-medium">
                    Ready to maximize your Kansas City investment? Our local experts are here to handle the heavy lifting while you grow your portfolio.
                  </p>

                  <div className="space-y-8">
                    {contactInfo.map((item, i) => <div key={i} className="flex gap-4 group" data-index={i}>
                        <div className="shrink-0 size-12 rounded bg-white/10 border border-white/5 flex items-center justify-center text-secondary group-hover:bg-secondary group-hover:text-secondary-foreground transition-colors duration-300">
                          <item.icon className="size-5" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-primary-foreground">{item.label}</h4>
                          <Link to={item.href} className="text-lg font-medium text-secondary hover:text-secondary/80 transition-colors block my-0.5">
                            {item.value}
                          </Link>
                          <p className="text-sm text-primary-foreground/60">{item.description}</p>
                        </div>
                      </div>)}
                  </div>
                </div>

                <div className="mt-12 pt-8 border-t border-white/10">
                  <p className="text-sm text-primary-foreground/40 font-mono">
                    © {new Date().getFullYear()} Invest KC Property Management.
                  </p>
                </div>
              </div>

              {}
              <div className="lg:col-span-3 p-8 md:p-12 bg-background">
                <div className="max-w-lg mx-auto lg:mx-0 lg:max-w-none h-full flex flex-col justify-center">
                  <div className="mb-8">
                    <h3 className="text-2xl font-bold tracking-tight mb-2 text-primary">Property Management Inquiry</h3>
                    <p className="text-muted-foreground">
                      Provide your property details below for a free management consultation and market analysis.
                    </p>
                  </div>

                  <FormProvider formKey="talk_property_form_contact" formId="talk_property_form_contact" sectionName="reach-out-section" defaultValues={{
                  name: "",
                  email: "",
                  phone: "",
                  organization: "",
                  subject: "",
                  message: ""
                }} validationRules={{
                  name: {
                    required: true
                  },
                  email: {
                    required: true,
                    pattern: "email"
                  },
                  subject: {
                    required: true
                  },
                  message: {
                    required: true
                  }
                }} submitText="Request Consultation" successMessage="Thank you! Our Kansas City team will review your property and contact you shortly.">
                    <ContactForm />
                  </FormProvider>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}